import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, CreditCard } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher } from "@/lib/hooks";
import { PageHeader, StatusBadge, Money, Empty } from "@/components/common";
import { ItemsEditor, cleanItems, CurrencySelect } from "@/components/FormBits";
import { Field } from "@/components/PropertyDialog";
import { DateInput } from "@/components/DateInput";

const TONE = { soumis: "pending", approuve: "paid", refuse: "unpaid", emise: "pending", payee: "paid", annulee: "terminated" };

export function DocStatus({ s, testId }) {
  const { t } = usePrefs();
  return <StatusBadge value={TONE[s]} label={t(s)} testId={testId} />;
}

export function QuoteActions({ q }) {
  const { user } = useAuth();
  const { t } = usePrefs();
  const qc = useQueryClient();
  if (q.status !== "soumis" || !["admin", "supervision", "gestionnaire", "proprietaire"].includes(user.role)) return null;
  const decide = async (status) => {
    try { await api.post(`/quotes/${q.id}/decision`, { status }); qc.invalidateQueries(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-1.5">
      <Button size="sm" className="h-7 rounded-sm bg-emerald-700 text-xs hover:bg-emerald-800" onClick={() => decide("approuve")} data-testid={`quote-approve-${q.id}`}>{t("approve")}</Button>
      <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => decide("refuse")} data-testid={`quote-reject-${q.id}`}>{t("reject")}</Button>
    </div>
  );
}

export function QuoteRow({ q, showTicket = true }) {
  const { fdate } = usePrefs();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`quote-row-${q.id}`}>
      <div className="min-w-0">
        <div className="font-semibold"><span className="mr-2 font-data text-xs text-slate-400">{q.number}</span>{showTicket ? q.ticket_title : q.items.map((i) => i.label).join(", ")}</div>
        <div className="text-xs text-slate-500">{q.provider_name} · {showTicket && `${q.ticket_number} · `}{fdate(q.created_at)}</div>
      </div>
      <div className="flex items-center gap-3"><Money amount={q.total} currency={q.currency} /><DocStatus s={q.status} testId={`quote-status-${q.id}`} /><QuoteActions q={q} /></div>
    </div>
  );
}

function TenantInvoiceDialog() {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const { data: leases = [] } = useQuery({ queryKey: ["leases"], queryFn: () => fetcher("/leases") });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ lease_id: "", currency: "EUR", due_date: "", items: [{ label: "", qty: 1, unit_price: "" }] });
  const save = async () => {
    try { await api.post("/invoices", { ...f, items: cleanItems(f.items) }); qc.invalidateQueries({ queryKey: ["invoices"] }); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-invoice-button"><Plus size={16} className="mr-1" />{t("invoiceTenant")}</Button></DialogTrigger>
      <DialogContent className="max-w-xl rounded-sm" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("invoiceTenant")}</DialogTitle></DialogHeader>
        <Field label={t("lease")}>
          <Select value={f.lease_id} onValueChange={(v) => setF({ ...f, lease_id: v, currency: leases.find((l) => l.id === v)?.currency || "EUR" })}>
            <SelectTrigger data-testid="invoice-lease-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{leases.filter((l) => l.status === "active").map((l) => <SelectItem key={l.id} value={l.id}>{l.tenant?.name} · {l.property?.name} {l.unit?.reference}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("currency")}><CurrencySelect value={f.currency} onChange={(v) => setF({ ...f, currency: v })} testId="invoice-currency-select" /></Field>
          <Field label={t("due")}><DateInput value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="invoice-due-input" /></Field>
        </div>
        <ItemsEditor value={f.items} onChange={(items) => setF({ ...f, items })} currency={f.currency} />
        <Button onClick={save} disabled={!f.lease_id || !f.due_date || !cleanItems(f.items).length} className="rounded-sm bg-[#0F172A]" data-testid="invoice-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceRow({ inv, staff }) {
  const { t, fdate } = usePrefs();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try { const { data } = await api.post("/payments/invoice-checkout", { invoice_id: inv.id, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const qc = useQueryClient();
  const setStatus = async (status) => { try { await api.post(`/invoices/${inv.id}/status`, { status }); qc.invalidateQueries({ queryKey: ["invoices"] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`invoice-row-${inv.id}`}>
      <div className="min-w-0">
        <div className="font-semibold"><span className="mr-2 font-data text-xs text-slate-400">{inv.number}</span>{inv.items.map((i) => i.label).join(", ")}</div>
        <div className="text-xs text-slate-500">{t(inv.kind === "locataire" ? "invoiceTenant" : "invoiceProvider")} · {inv.issuer_name} → {inv.recipient_name} · {t("due")} {fdate(inv.due_date)}</div>
      </div>
      <div className="flex items-center gap-3">
        <Money amount={inv.total} currency={inv.currency} /><DocStatus s={inv.status} testId={`invoice-status-${inv.id}`} />
        {inv.recipient_id === user.id && inv.status === "emise" && <Button size="sm" disabled={busy} onClick={pay} className="h-7 rounded-sm bg-[#0F172A] text-xs hover:bg-[#B8860B]" data-testid={`invoice-pay-${inv.id}`}><CreditCard size={12} className="mr-1" />{t("pay")}</Button>}
        {staff && inv.status === "emise" && <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => setStatus("payee")} data-testid={`invoice-paid-${inv.id}`}>{t("markPaid")}</Button>}
        {staff && inv.status === "emise" && <button className="text-xs font-semibold text-slate-600 hover:text-rose-600" onClick={() => setStatus("annulee")} data-testid={`invoice-cancel-${inv.id}`}>{t("cancel")}</button>}
      </div>
    </div>
  );
}

export default function Finance() {
  const { user } = useAuth();
  const { t } = usePrefs();
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  const { data: quotes = [] } = useQuery({ queryKey: ["quotes"], queryFn: () => fetcher("/quotes"), enabled: user.role !== "locataire" });
  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: () => fetcher("/invoices") });
  return (
    <div data-testid="finance-page">
      <PageHeader eyebrow={t("secFinance")} title={t("finance")}>{staff && <TenantInvoiceDialog />}</PageHeader>
      <Tabs defaultValue={user.role === "locataire" ? "invoices" : "quotes"}>
        <TabsList className="mb-5 rounded-sm">
          {user.role !== "locataire" && <TabsTrigger value="quotes" data-testid="finance-tab-quotes">{t("quotes")} ({quotes.length})</TabsTrigger>}
          <TabsTrigger value="invoices" data-testid="finance-tab-invoices">{t("invoices")} ({invoices.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="quotes">{quotes.length ? <div className="divide-y divide-slate-100 rounded-sm border bg-white">{quotes.map((q) => <QuoteRow key={q.id} q={q} />)}</div> : <Empty />}</TabsContent>
        <TabsContent value="invoices">{invoices.length ? <div className="divide-y divide-slate-100 rounded-sm border bg-white">{invoices.map((i) => <InvoiceRow key={i.id} inv={i} staff={staff} />)}</div> : <Empty />}</TabsContent>
      </Tabs>
    </div>
  );
}
